import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditState from "./AddEditState";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import StateActionButton from "./StateActionButton";
import Confirmation from "../../../components/Confirmation";

const States = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [stateId, setStateId] = useState(null);
  const [showAddEditState, setShowAddEditState] = useState(false);
  const [showStateDelete, setShowStateDelete] = useState(false);

  const toggleShowAddEditState = () => {
    if (showAddEditState) {
      setEdit(false);
      setStateId(null);
    }
    setShowAddEditState(!showAddEditState);
  };

  const toggleShowDelete = () => {
    if (showStateDelete) {
      setStateId(null);
    }
    setShowStateDelete(!showStateDelete);
  };

  const fetchState = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_STATE_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const statesList = data.data.data;
        setStatesList(statesList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setStatesList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const statesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: statesList[i]?.id,
        short_name: statesList[i]?.short_name ? statesList[i]?.short_name : "",
        name: statesList[i]?.name ? statesList[i]?.name : "",
        action: (
          <>
            <StateActionButton
              id={statesList[i]?.id}
              setEdit={setEdit}
              is_used={statesList[i]?.is_used}
              setStateId={setStateId}
              toggleShowAddEditState={toggleShowAddEditState}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const states = statesGenerator(statesList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "short_name",
      text: "Short Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchState(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchState(limit, page);
  };

  // Single State Delete
  const handleStateDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_STATE_LIST}/${stateId}`);
      setLoading(false);
      fetchState();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add State */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit State" : "Add State"}
              handleOpen={() => {}}
              handleClose={() => {}}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add State</span>
                </div>
              }
              className={"sm:max-w-3/4"}
              open={showAddEditState}
              buttonClassName={""}
              setOpen={setShowAddEditState}
            >
              <AddEditState
                edit={edit}
                toggleShowAddEditState={toggleShowAddEditState}
                id={stateId}
                fetchStateData={() => fetchState(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={states}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete State"}
        open={showStateDelete}
        setOpen={setShowStateDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete state?"}
        okAction={async () => {
          handleStateDelete();
          toggleShowDelete();
          fetchState();
        }}
        cancelAction={() => {
          setStateId(null);
          setShowStateDelete(!showStateDelete);
        }}
      />
    </>
  );
};

export default States;
